import { IUserWeightedLeaderboardBreakdown } from 'fitbeat.models';
import React, { FunctionComponent, useEffect } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet, Text, View,
} from 'react-native';
import { connect } from 'react-redux';
import { IAppStore } from '../HomeWorkouts';
import { LeaderboardOverview } from '../SummaryDashboard/components/LeaderboardOverview';
import Theme from '../theme';
import { loadUserBreakdownForLeaderboard as loadUserBreakdownForLeaderboardAction } from '../Tracking/actions';
import { createUserServiceClient } from '../utils/apiClientFactory';
import { defaultScale } from '../utils/scaling';
import {
  calculateFormattedTotals,
  determineIndividualWorkoutInfo,
  determineUserBreakdownMetrics,
} from './LeaderboardUIHelper';

interface IProps {
  navigation: any;
  loadUserBreakdownForLeaderboard: (
    userProfileId,
    leaderboardConfigId,
    startDate,
    endDate,
    team,
  ) => IUserWeightedLeaderboardBreakdown[];
  userBreakdown: IUserWeightedLeaderboardBreakdown[];
  isLoadingUserBreakdown: boolean;
}

export const LeaderboardUserBreakdown: FunctionComponent<IProps> = (
  {
    navigation,
    loadUserBreakdownForLeaderboard,
    userBreakdown,
    isLoadingUserBreakdown,
  },
) => {
  const {
    userProfileId,
    leaderboardId,
    startDate,
    endDate,
    t,
    isFeatured,
    leaderboard,
    team,
  } = navigation.state.params;
  useEffect(() => {
    loadUserBreakdownForLeaderboard(
      userProfileId,
      leaderboardId,
      startDate,
      endDate,
      team,
    );
  }, []);

  const { totalScoreFormatted, contributionScoreFormatted } = calculateFormattedTotals(userBreakdown);
  const { metricAbr, metric } = determineUserBreakdownMetrics(leaderboard.metric, totalScoreFormatted);

  if (isLoadingUserBreakdown) {
    return (
      <ActivityIndicator
        size='large'
        animating
        color={Theme.PRIMARY}
        style={styles.loader}
      />
    );
  }
  return (
    <View style={styles.container}>
      <LeaderboardOverview
        userLeaderboardData={leaderboard}
        t={t}
        showIcons={false}
        showDivider
        allowPress={false}
        teamMembersOverview={false}
        isFeatured={isFeatured}
        showJoinLeaderboardTile={false}
        isTeamLeaderboard={false}
        userBreakdown={userBreakdown}
      />
      <View style={styles.tableContainer}>
        <View style={styles.rowContainer}>
          <View style={styles.leftColumn}>
            <Text style={styles.boldSubText}>DATE</Text>
          </View>
          <View style={[styles.middleColumn, { marginLeft: defaultScale(10) }]}>
            <Text style={styles.boldSubText}>{metric.toUpperCase()}</Text>
          </View>
          <View style={styles.rightColumn}>
            <Text style={styles.boldSubText}>{t('tracking:scoreContributionHeader')}</Text>
          </View>
        </View>
        <View style={[styles.bottomLine, { marginTop: defaultScale(10) }]} />
        <ScrollView style={{ flex: 1 }}>
          {userBreakdown && userBreakdown.map((item, index) => {
            const individualScoreInfo = determineIndividualWorkoutInfo(item, leaderboard.metric);

            return (
              <View>
                <View style={styles.rowContainer} key={index}>
                  <View style={styles.leftColumn}>
                    <Text style={styles.mainText}>{individualScoreInfo.dateHeader}</Text>
                    <Text style={styles.subText}>{individualScoreInfo.dateSubHeader}</Text>
                  </View>
                  <View style={[styles.middleColumn, { marginLeft: defaultScale(10) }]}>
                    <Text style={styles.mainText}>{individualScoreInfo.totalScoreForWorkoutFormatted}</Text>
                  </View>
                  <View style={styles.rightColumn}>
                    <Text style={styles.mainBoldText}>{individualScoreInfo.contributedScoreForWorkoutFormatted}</Text>
                    <Text style={styles.subText}>{individualScoreInfo.contributionSubText}</Text>
                  </View>
                  <View style={styles.bottomLine} />
                </View>
                <View style={styles.bottomLine} />
              </View>
            );
          })}
        </ScrollView>
        <View style={styles.totalContainer}>
          <View style={styles.leftColumn}>
            <Text style={styles.mainBoldText}>{t('tracking:totalHeader')}</Text>
          </View>
          <View style={[styles.middleColumn, { marginLeft: defaultScale(10) }]}>
            <Text style={[styles.mainBoldText, { flexShrink: 1 }]} numberOfLines={1}>
              {`${totalScoreFormatted} ${metricAbr}`}
            </Text>
          </View>
          <View style={styles.rightColumn}>
            <Text style={styles.mainBoldText}>{contributionScoreFormatted}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.WHITE,
    marginBottom: defaultScale(10),
    padding: defaultScale(8),
    flex: 1,
  },
  loader: {
    height: defaultScale(300),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: defaultScale(20),
    alignSelf: 'center',
  },
  subText: {
    ...Theme.textStyle.small,
    color: Theme.GREY,
  },
  boldSubText: {
    ...Theme.textStyle.smallBold,
    color: Theme.GREY,
  },
  mainText: {
    ...Theme.textStyle.bodyCopyMed,
  },
  mainBoldText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  rowContainer: {
    flexDirection: 'row',
    paddingHorizontal: defaultScale(5),
    marginVertical: defaultScale(5),
  },
  tableContainer: {
    paddingHorizontal: defaultScale(18),
    flex: 1,
  },
  totalContainer: {
    flexDirection: 'row',
    marginTop: defaultScale(5),
    paddingVertical: defaultScale(8),
    paddingHorizontal: defaultScale(5),
    borderRadius: defaultScale(4),
    backgroundColor: Theme.GREY_LIGHT,
  },
  rightColumn: {
    flex: 3,
    alignItems: 'flex-end',
  },
  middleColumn: {
    flex: 2,
    alignItems: 'flex-start',
  },
  leftColumn: {
    flex: 1,
    alignItems: 'flex-start',
  },
  bottomLine: {
    backgroundColor: Theme.GREY_LIGHT,
    height: 1,
  },
});

const mapStateToProps = (state: IAppStore) => {
  const { isLoadingUserBreakdown } = state.tracking.view;
  const { userBreakdown } = state.tracking.data;
  return {
    isLoadingUserBreakdown,
    userBreakdown,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  const userServiceClient = createUserServiceClient();
  return {
    loadUserBreakdownForLeaderboard: (
      userProfileId,
      leaderboardConfigId,
      startDate,
      endDate,
      team,
    ) => dispatch(
      loadUserBreakdownForLeaderboardAction(
        userServiceClient,
        userProfileId,
        leaderboardConfigId,
        startDate,
        endDate,
        team,
      ),
    ),
  };
};

export const LeaderboardUserBreakdownScreen = connect(mapStateToProps, mapDispatchToProps)(LeaderboardUserBreakdown);
